import { useEffect, useRef, useState, type JSX } from 'react';

/**
 * React host for the hero living vignette (see {@link mountLandingScene}). Mirrors the proven
 * {@link LivingBackdrop} lifecycle, with two landing-specific concerns:
 *
 *  1. LAZY PIXI — Pixi.js is heavy and must NOT sit in the hero's critical render path. We `import()` the
 *     scene module dynamically inside the effect, so the page text + CTA paint first and Pixi loads after.
 *  2. NO LAYOUT SHIFT — the host reserves its box (the parent sizes it), and a tasteful CSS-gradient +
 *     emoji-duck fallback fills that exact box from the very first paint. The Pixi canvas then layers on
 *     top once ready; if Pixi can't initialise (no WebGL) the fallback simply stays. Either way the box
 *     never resizes, so there's no CLS.
 *
 * Decorative: `aria-hidden`, never in the tab order. The interactive layers above it own pointer events;
 * the canvas itself doesn't need them (parallax is tracked on this host element by the scene).
 *
 * Dev observability: sets `window.__duckLanding.sceneAlive` true once the scene paints its first frame OR
 * falls back, so the e2e agent can assert "the hero is alive (or gracefully degraded)".
 */
export function LandingScene(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  // Tracks whether the live Pixi scene mounted, purely to fade the CSS fallback out from under it once the
  // real world is up (so a WebGL-capable visitor sees the actual product, not the placeholder).
  const [sceneUp, setSceneUp] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cancelled = false;
    let handle: { destroy(): void } | null = null;

    /** Flip the dev flag the e2e agent polls — once the scene is alive OR we've decided to fall back. */
    const markReady = (sceneAlive: boolean): void => {
      if (typeof window !== 'undefined' && import.meta.env?.DEV) {
        (window as unknown as { __duckLanding?: { ready: boolean; sceneAlive: boolean } }).__duckLanding = {
          ready: true,
          sceneAlive,
        };
      }
    };

    // Lazy-load Pixi + the scene off the critical path. A failed import (or no WebGL → null handle) leaves
    // the CSS fallback in place; we still mark ready so the e2e agent never hangs.
    void import('./landing-scene')
      .then(({ mountLandingScene }) => mountLandingScene(el))
      .then((h) => {
        if (cancelled) {
          h?.destroy();
          return;
        }
        handle = h;
        const alive = h !== null;
        if (alive) setSceneUp(true);
        markReady(alive);
      })
      .catch(() => {
        if (!cancelled) markReady(false); // import/init failed → fallback stays; not a hang
      });

    return () => {
      cancelled = true;
      handle?.destroy();
      if (typeof window !== 'undefined' && import.meta.env?.DEV) {
        delete (window as unknown as { __duckLanding?: unknown }).__duckLanding;
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      data-landing-scene="true"
      aria-hidden="true"
      className="absolute inset-0 overflow-hidden rounded-3xl"
    >
      {/* CSS fallback — shown from the first paint, reserving the box (no CLS). Fades out once the live
          Pixi world is up; stays put if WebGL is unavailable. Pure gradient + a couple of resting ducks. */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ${sceneUp ? 'opacity-0' : 'opacity-100'}`}
        style={{
          background:
            'radial-gradient(120% 120% at 70% 10%, rgba(255,210,63,0.16), transparent 55%),' +
            'radial-gradient(90% 90% at 20% 90%, rgba(90,169,230,0.16), transparent 60%),' +
            'linear-gradient(160deg, #18224a 0%, #121a36 60%, #0d1428 100%)',
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center gap-6 text-5xl opacity-80 sm:text-6xl">
          <span className="-rotate-6">🦆</span>
          <span className="translate-y-3">🦊</span>
          <span className="-translate-y-2">🐸</span>
        </div>
      </div>
    </div>
  );
}
